import { useEffect, useState } from 'react';
import { api, UnauthorizedError } from '../api';

type Info = {
  configured: boolean;
  apiUrl: string | null;
  managerUrl: string | null;
  instance: string | null;
  state: string | null;
};

type QrData = {
  base64?: string;
  pairingCode?: string;
  code?: string;
  state?: string;
};

type Props = {
  onSessionExpired: () => void;
  isAdmin?: boolean;
};

type Toast = { kind: 'ok' | 'err'; text: string };
type Pending = 'connect' | 'restart' | 'logout' | 'delete' | null;

export function EvolutionApi({ onSessionExpired, isAdmin }: Props) {
  const [info, setInfo] = useState<Info | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [qr, setQr] = useState<QrData | null>(null);
  const [confirm, setConfirm] = useState<null | 'logout' | 'delete'>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .whatsappInfo()
      .then((r) => setInfo(r.data))
      .catch((e) => {
        if (e instanceof UnauthorizedError) onSessionExpired();
        else setError(String(e.message || e));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flash = (t: Toast) => {
    setToast(t);
    window.setTimeout(() => setToast(null), 4000);
  };

  const handle = async (action: Exclude<Pending, null>) => {
    setPending(action);
    setToast(null);
    try {
      if (action === 'connect') {
        const r = await api.whatsappConnect();
        setQr(r.data);
      } else if (action === 'restart') {
        await api.whatsappRestart();
        flash({ kind: 'ok', text: 'Instância reiniciada.' });
      } else if (action === 'logout') {
        await api.whatsappLogout();
        flash({ kind: 'ok', text: 'Sessão WhatsApp encerrada.' });
      } else if (action === 'delete') {
        await api.whatsappDeleteInstance();
        flash({ kind: 'ok', text: 'Instância apagada no servidor Evolution.' });
      }
      load();
    } catch (e: any) {
      if (e instanceof UnauthorizedError) onSessionExpired();
      else flash({ kind: 'err', text: String(e?.message || e) });
    } finally {
      setPending(null);
      setConfirm(null);
    }
  };

  const isOpen = info?.state === 'open';

  return (
    <div className="evo" data-testid="evo-screen">
      <header className="evo-head">
        <div>
          <div className="evo-title">Evolution API</div>
          <div className="evo-sub">
            Gateway WhatsApp (Baileys) que alimenta a aba <strong>Conversas</strong>.
          </div>
        </div>
        <button className="btn" onClick={load} title="Recarregar" disabled={!!pending}>
          ↻
        </button>
      </header>

      {error && <div className="error">{error}</div>}
      {toast && <div className={`evo-toast evo-toast-${toast.kind}`}>{toast.text}</div>}

      {loading && <div className="muted">carregando…</div>}

      {!loading && info && !info.configured && (
        <div className="evo-warning">
          Variáveis <code>EVOLUTION_API_URL</code>, <code>EVOLUTION_API_KEY</code> e{' '}
          <code>EVOLUTION_INSTANCE_NAME</code> não estão configuradas no backend. Configure no Worker
          para habilitar a integração.
        </div>
      )}

      {!loading && info && info.configured && (
        <>
          <div className="evo-grid">
            <InfoRow label="Instância" value={info.instance || '—'} mono />
            <InfoRow label="Estado" value={stateLabel(info.state)} pillClass={`evo-state-${info.state || 'unknown'}`} />
            <InfoRow label="API URL" value={info.apiUrl || '—'} mono link={info.apiUrl} />
            <InfoRow label="Manager" value={info.managerUrl || '—'} mono link={info.managerUrl} />
          </div>

          <div className="evo-actions">
            {info.managerUrl && (
              <a
                className="btn primary"
                href={info.managerUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-manager"
              >
                Abrir Manager →
              </a>
            )}
            {info.apiUrl && (
              <a className="btn" href={info.apiUrl} target="_blank" rel="noopener noreferrer">
                Abrir API root
              </a>
            )}
          </div>

          {isAdmin ? (
            <div className="evo-crud">
              <div className="evo-crud-title">Ações da instância</div>
              <div className="evo-crud-row">
                <button
                  className="btn"
                  onClick={() => handle('connect')}
                  disabled={!!pending || isOpen}
                  title={isOpen ? 'Instância já está conectada' : 'Gerar QR Code / pairing code'}
                  data-testid="evo-connect"
                >
                  {pending === 'connect' ? 'Gerando…' : 'Conectar (QR)'}
                </button>
                <button
                  className="btn"
                  onClick={() => handle('restart')}
                  disabled={!!pending}
                  data-testid="evo-restart"
                >
                  {pending === 'restart' ? 'Reiniciando…' : 'Reiniciar'}
                </button>
                <button
                  className="btn"
                  onClick={() => setConfirm('logout')}
                  disabled={!!pending || !isOpen}
                  title={!isOpen ? 'Instância não está conectada' : 'Encerrar sessão WhatsApp'}
                  data-testid="evo-logout"
                >
                  {pending === 'logout' ? 'Encerrando…' : 'Desconectar'}
                </button>
                <button
                  className="btn danger"
                  onClick={() => setConfirm('delete')}
                  disabled={!!pending}
                  data-testid="evo-delete"
                >
                  {pending === 'delete' ? 'Apagando…' : 'Apagar instância'}
                </button>
              </div>
              <div className="evo-crud-hint">
                <strong>Conectar</strong> gera QR / pairing code para parear o número.{' '}
                <strong>Reiniciar</strong> recarrega o socket Baileys (mantém a sessão).{' '}
                <strong>Desconectar</strong> faz logout do WhatsApp (a instância continua).{' '}
                <strong>Apagar</strong> remove a instância do servidor — só recrie pelo Manager ou via API.
              </div>
            </div>
          ) : (
            <div className="evo-crud-locked">
              Ações de instância (conectar / reiniciar / apagar) estão disponíveis apenas para admins.
            </div>
          )}

          <div className="evo-note">
            O Manager é a interface web da própria Evolution API (servida em <code>/manager</code>).
            Use-o para parear o número via QR Code, ver eventos de webhook ou reiniciar a instância.
            Não compartilhe a <code>apikey</code> do header — ela controla todas as instâncias do
            servidor.
          </div>
        </>
      )}

      {qr && <QrModal data={qr} instance={info?.instance || ''} onClose={() => setQr(null)} />}

      {confirm && (
        <ConfirmModal
          kind={confirm}
          instance={info?.instance || ''}
          busy={!!pending}
          onCancel={() => setConfirm(null)}
          onConfirm={() => handle(confirm)}
        />
      )}
    </div>
  );
}

function QrModal({ data, instance, onClose }: { data: QrData; instance: string; onClose: () => void }) {
  const src = normalizeQr(data);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card evo-qr-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">Parear WhatsApp — {instance}</div>
          <button className="btn-icon" onClick={onClose} title="Fechar">✕</button>
        </div>
        {src ? (
          <img className="evo-qr-img" src={src} alt="QR Code WhatsApp" />
        ) : (
          <div className="muted" style={{ padding: 16 }}>
            QR Code indisponível. {data.state === 'open' ? 'A instância já está conectada.' : 'Tente novamente em alguns segundos.'}
          </div>
        )}
        {data.pairingCode && (
          <div className="evo-qr-pairing">
            <span>Pairing code:</span> <code>{data.pairingCode}</code>
          </div>
        )}
        <div className="evo-qr-hint">
          No WhatsApp do celular: <strong>Configurações → Aparelhos conectados → Conectar aparelho</strong>.
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({
  kind,
  instance,
  busy,
  onCancel,
  onConfirm,
}: {
  kind: 'logout' | 'delete';
  instance: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const isDelete = kind === 'delete';
  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onCancel}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-title">
            {isDelete ? 'Apagar instância?' : 'Desconectar instância?'}
          </div>
        </div>
        <div className="modal-body">
          {isDelete ? (
            <>
              Isso remove permanentemente <code>{instance}</code> do servidor Evolution. Conversas e
              estado do Baileys serão perdidos — só recrie pelo Manager.
            </>
          ) : (
            <>
              Isso encerra a sessão WhatsApp de <code>{instance}</code>. O número precisará ser pareado
              novamente via QR Code.
            </>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button className={isDelete ? 'btn danger' : 'btn primary'} onClick={onConfirm} disabled={busy}>
            {busy ? 'Aguarde…' : isDelete ? 'Apagar' : 'Desconectar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function normalizeQr(d: QrData): string | undefined {
  if (!d.base64) return undefined;
  return d.base64.startsWith('data:') ? d.base64 : `data:image/png;base64,${d.base64}`;
}

function InfoRow({
  label,
  value,
  mono,
  link,
  pillClass,
}: {
  label: string;
  value: string;
  mono?: boolean;
  link?: string | null;
  pillClass?: string;
}) {
  return (
    <div className="evo-row">
      <div className="evo-row-label">{label}</div>
      <div className={`evo-row-value ${mono ? 'mono' : ''}`}>
        {pillClass ? (
          <span className={`evo-pill ${pillClass}`}>{value}</span>
        ) : link ? (
          <a href={link} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

function stateLabel(state: string | null): string {
  if (!state) return 'desconhecido';
  if (state === 'open') return 'conectado';
  if (state === 'close') return 'desconectado';
  if (state === 'connecting') return 'conectando…';
  return state;
}
